import { TouchableOpacity } from 'react-native-web';

import {
    React,
    Text
} from './common';
import {
    CHILD_CLINIC_HISTORY_KEY,
    ONLINE_BOOKING_CHILD_CLINIC,
    ONLINE_BOOKING_CLINIC,
    USER_CLINIC_HISTORY_KEY
} from './hooks/usePersonalData';
import { parseLinks } from './i18n';
import { openURL } from './services/device';
import { COLORS } from './styles/colors';

const prefixesRaw = {
    aibolitPrefix: 'rawAibolitPrefix',
    aibolit: 'rawAibolit',
};

export const prefixes = new Proxy(prefixesRaw, {
    get(target, prop) {
        if (prop === 'aibolitPrefix') {
            return Object.R?.('titles.iGive', { clinic: Object.R('titles.aibolit') });
        } else if (prop === 'aibolit') {
            return Object.R?.('titles.aibolit');
        }
    },
});


export const clinicCapabilitiesMapping = {
    OnlineBookingConsentGathering: {
        main: ONLINE_BOOKING_CLINIC,
        family: ONLINE_BOOKING_CHILD_CLINIC,
    },
    MedicalHistoryConsentGathering: {
        main: USER_CLINIC_HISTORY_KEY,
        family: CHILD_CLINIC_HISTORY_KEY,
    },
};

const RObject = {
    R: Object.R,
};

const R = new Proxy(RObject, {
    get(target, prop) {
        if (prop === 'R') {
            return Object.R;
        }
        return target[prop];
    },
});

export const HRef = ({
    href,
    style,
    children,
}) => {
    return <Text style={style}><TouchableOpacity
        onPress={() => openURL(href)}
    >{children}</TouchableOpacity></Text>;
};

export const TextWithLinks = ({
    data,
    linkStyle,
    numberOfLines = 0,
    style,
}) => {
    const parsed = parseLinks(data);
    if (!parsed?.links?.length) {
        return <Text
            numberOfLines={numberOfLines}
            style={style}>{parsed.text}</Text>;
    }

    const parts = parsed.text.split(/(\{link\d+})/);

    const content = parts.map((part, index) => {
        const linkMatch = part.match(/\{link(\d+)}/);
        if (linkMatch) {
            const linkIndex = parseInt(linkMatch[1], 10) - 1;
            const linkData = parsed.links[linkIndex];
            if (linkData) {
                return (
                    <Text
                        key={index}
                        style={[ {
                            color: COLORS.HREF,
                            textDecorationLine: 'underline',
                        },
                        linkStyle ]}
                        onPress={() => openURL(linkData.link)}
                    >
                        {linkData.linkText}
                    </Text>
                );
            }
        }
        return part;
    });

    return <Text
        numberOfLines={numberOfLines}
        style={[ style ]}>{content}</Text>;
};

export const getLinkText = link => {
    // treat null and undefined as empty
    if (link === null || typeof link === 'undefined') {
        return '';
    }

    let str;
    if (typeof link === 'string') {
        str = link;
    } else if (typeof link === 'number' || typeof link === 'boolean' || typeof link === 'bigint') {
        str = String(link);
    } else if (typeof link === 'object') {
        if (typeof link.toString === 'function' && link.toString !== Object.prototype.toString) {
            str = String(link);
        } else {
            try {
                str = JSON.stringify(link);
            } catch (e) {
                str = '';
            }
            if (str === '{}' || str === '[object Object]') {
                str = '';
            }
        }
    } else {
        try {
            str = String(link);
        } catch (e) {
            str = '';
        }
    }

    const trimmed = str.trim();

    // collect all bracketed parts: [ ... ]
    const bracketMatches = Array.from(trimmed.matchAll(/\[([^\]]*)]/g));
    if (bracketMatches.length > 0) {
        // return only inner parts (before :: if present), joined by space
        const parts = bracketMatches.map(m => {
            const inside = m[1] ?? '';
            const idx = inside.indexOf('::');
            const text = idx !== -1 ? inside.slice(0, idx) : inside;
            return text.replace(/\s{2,}/g, ' ')
                .trim();
        })
            .filter(Boolean);
        return parts.join(' ')
            .replace(/\s{2,}/g, ' ')
            .trim();
    }

    // no bracketed parts -> return normalized full string
    return trimmed.replace(/\s{2,}/g, ' ')
        .trim();
};

export const getConsentData = (key, clinic) => {
    const consentKey = `consents.${key}`;
    const result = {
        key,
        clinicName: clinic,
        clinic,
        text: R.R(consentKey, {
            prefix: '',
            aibolitPrefix: prefixes.aibolitPrefix,
            aibolit: prefixes.aibolit,
            clinic,
            clinicPrefix: R.R('titles.iGive', { clinic }),
        }),
        textWithPrefix: R.R(consentKey, {
            prefix: prefixes.aibolit,
            aibolitPrefix: prefixes.aibolitPrefix,
            aibolit: prefixes.aibolit,
            clinic,
            clinicPrefix: R.R('titles.iGive', { clinic }),
        }),
        textWithoutPrefix: getLinkText(R.R(consentKey, {
            prefix: '',
            aibolitPrefix: '',
            aibolit: prefixes.aibolit,
            clinic,
            clinicPrefix: R.R('titles.iGive', { clinic }),
        })),
        link: R.R(`links.${key}`),
        prefix: prefixes.aibolit,
    };
    const TextData = TextWithLinks({ data: result?.text });
    result.links = parseLinks(result.text);
    result.TextData = TextData;
    return result;
};
